// src/lib/appNav.ts
//
// The module navigation model for the global app sidebar. Mirrors the old
// LandingPage TILES (same feature ids so useModuleAccess gates them the same
// way) and adds the per-module sub-tabs so the sidebar can expand each module
// to its sections. Clicking a sub-tab deep-links via navStore into the module.

import type { ComponentType } from 'react';
import {
  BarChart3, BookOpen, Truck, Database, Server, Shield,
  User as UserIcon, LifeBuoy, CreditCard, ChefHat, Landmark,
} from 'lucide-react';
import type { Page } from '../App';

export interface NavSubTab {
  section: string;        // the module page's internal section key
  label: string;
  feature: string | null; // null = always shown when the module is visible
  hideWhenTenant?: string; // hidden when this tenant-level flag is on
}

export interface NavModule {
  id: string;             // featureCatalog id (also the gating key)
  page: Page;             // top-level Page this module routes to
  label: string;
  icon: ComponentType<{ size?: number }>;
  sysadminOnly?: boolean; // System Admin manages OTHER tenants — admins only
  subTabs?: NavSubTab[];  // present for the multi-section modules
}

export const NAV_MODULES: NavModule[] = [
  {
    id: 'analytics', page: 'analyticsV2', label: 'Analytics', icon: BarChart3,
    subTabs: [
      { section: 'overview',   label: 'Overview',   feature: null },
      { section: 'sales',      label: 'Sales',      feature: null },
      { section: 'ar',         label: 'A/R',        feature: null },
      { section: 'ap',         label: 'A/P',        feature: 'analytics.ap' },
      { section: 'orders',     label: 'Orders',     feature: null },
      { section: 'customers',  label: 'Customers',  feature: null },
      { section: 'inventory',  label: 'Inventory',  feature: 'analytics.inventory' },
      { section: 'purchasing', label: 'Purchasing', feature: 'analytics.purchasing' },
      { section: 'ai_orders',  label: 'AI Orders',  feature: 'ai_orders' },
    ],
  },
  {
    id: 'financials', page: 'financials', label: 'Financials', icon: Landmark,
    subTabs: [
      // financials.qbo (QuickBooks Online tenants) keeps only P&L + Balance Sheet.
      { section: 'overview',     label: 'Overview',      feature: null, hideWhenTenant: 'financials.qbo' },
      { section: 'pnl',          label: 'Profit & Loss', feature: null },
      { section: 'balancesheet', label: 'Balance Sheet', feature: 'financials.balancesheet' },
      { section: 'ar',           label: 'A/R Aging',     feature: null, hideWhenTenant: 'financials.qbo' },
      { section: 'ap',           label: 'A/P Aging',     feature: null, hideWhenTenant: 'financials.qbo' },
      { section: 'budget',       label: 'Budget',        feature: 'financials.budget', hideWhenTenant: 'financials.qbo' },
      { section: 'payroll',      label: 'Payroll',       feature: 'financials.payroll', hideWhenTenant: 'financials.qbo' },
      { section: 'timecards',    label: 'Time Cards',    feature: 'financials.payroll', hideWhenTenant: 'financials.qbo' },
      { section: 'connection',   label: 'Connection',    feature: null, hideWhenTenant: 'financials.qbo' },
    ],
  },
  {
    id: 'production', page: 'production', label: 'Production', icon: ChefHat,
    subTabs: [
      { section: 'dashboard',  label: 'Dashboard',  feature: null },
      { section: 'items',      label: 'Items',      feature: null },
      { section: 'recipes',    label: 'Recipes',    feature: null },
      { section: 'products',   label: 'Products',   feature: null },
      { section: 'planning',   label: 'Planning',   feature: null },
      { section: 'production', label: 'Production', feature: null },
      { section: 'settings',   label: 'Settings',   feature: null },
    ],
  },
  { id: 'routeReports',  page: 'routeReports',  label: 'Load / Route Sheets', icon: Truck },
  { id: 'quickbooks',    page: 'quickbooks',    label: 'QuickBooks Online',   icon: BookOpen },
  { id: 'customReports', page: 'customReports', label: 'Custom Reports',      icon: Database },
  { id: 'systemStatus',  page: 'systemStatus',  label: 'System Status',       icon: Server },
  { id: 'systemAdmin',   page: 'systemAdmin',   label: 'System Admin',        icon: Shield, sysadminOnly: true },
  { id: 'userControl',   page: 'userControl',   label: 'My Account',          icon: UserIcon },
  { id: 'support',       page: 'support',       label: 'Support',             icon: LifeBuoy },
  { id: 'billing',       page: 'billing',       label: 'Billing',             icon: CreditCard },
];
